import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CalendlyConnectionResponse, CalendlySyncResult } from '@orion/contracts';
import { api } from '../../../lib/api-client.js';

const KEY = ['calendly-connections'];

export function useCalendlyConnectionsQuery() {
  return useQuery({ queryKey: KEY, queryFn: () => api.get<CalendlyConnectionResponse[]>('/calendly/connections') });
}

/** Takes a pasted Calendly Personal Access Token rather than redirecting
 * through an OAuth consent screen — see `CalendlyConnectionsService`'s doc
 * comment for why. */
export function useConnectCalendly() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (token: string) => api.post<CalendlyConnectionResponse>('/calendly/connections', { token }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

/** One-time backfill for bookings made before this connection's webhook
 * subscription existed — see `CalendlyConnectionsService.sync()`'s doc
 * comment. Invalidates `['meetings']` too, not just the connections list,
 * since a sync's whole point is making new `CalendlyMeeting` rows show up
 * on the Calendar page. */
export function useSyncCalendlyConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<CalendlySyncResult>(`/calendly/connections/${id}/sync`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEY });
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
    },
  });
}

export function useDisconnectCalendly() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<void>(`/calendly/connections/${id}/disconnect`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteCalendlyConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/calendly/connections/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}
